import { useEffect, useRef, useState } from "react";

import type { Project } from "@/lib/portfolio";

/**
 * Card cover: a silently looping MP4 when the project has one in WordPress,
 * otherwise the WordPress featured image.
 *
 * Visual model (this is what keeps animated cards from flashing grey):
 *
 * - A cover mounts its `<video>` shortly before the card reaches the viewport.
 * - As soon as the video decodes its first frame it becomes visible. It does
 *   NOT wait for playback, because the marquee deliberately keeps only a couple
 *   of videos playing at a time on phones, and a paused video still shows its
 *   last decoded frame.
 * - Once a cover has painted, the element stays mounted for the rest of the
 *   session. Unmounting was cheaper on memory but always fell back to grey for
 *   projects with no WordPress featured image, which is all of the animated
 *   ones. Playback (and therefore decoder pressure) is still bounded by the
 *   marquee's play/pause scheduler.
 * - Source selection happens in JS: browsers ignore the `media` attribute on
 *   `<source>` inside `<video>`, so phones were not reliably getting the small
 *   mobile variant.
 */

const CLASS = "h-full w-full object-cover transition-transform duration-500 group-hover:scale-105";

/** Mount a little before the card is on screen so playback starts in time. */
const PRELOAD_MARGIN_PHONE = 200;
const PRELOAD_MARGIN_DESKTOP = 400;

type Entry = {
  el: HTMLElement;
  setActive: (active: boolean) => void;
};

const entries = new Set<Entry>();
let frame = 0;

const PHONE_QUERY = "(max-width: 1023px)";

function isPhone() {
  return typeof window !== "undefined" && window.matchMedia(PHONE_QUERY).matches;
}

function schedule() {
  if (typeof window === "undefined" || frame) return;
  frame = window.requestAnimationFrame(() => {
    frame = 0;
    const margin = isPhone() ? PRELOAD_MARGIN_PHONE : PRELOAD_MARGIN_DESKTOP;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    entries.forEach((entry) => {
      const rect = entry.el.getBoundingClientRect();
      const nearVertically = rect.bottom > -margin && rect.top < vh + margin;
      const nearHorizontally = rect.right > -margin && rect.left < vw + margin;
      entry.setActive(nearVertically && nearHorizontally);
    });
  });
}

function register(entry: Entry) {
  entries.add(entry);
  if (entries.size === 1 && typeof window !== "undefined") {
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
  }
  schedule();
  return () => {
    entries.delete(entry);
    if (entries.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    }
  };
}

export function ProjectCover({ project, still = false }: { project: Project; still?: boolean }) {
  const hasVideo = Boolean(project.coverVideo || project.mobileCoverVideo);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  /** True once the video has decoded a frame; from then on it stays mounted. */
  const [painted, setPainted] = useState(false);
  const [phone, setPhone] = useState<boolean | null>(null);

  useEffect(() => {
    if (still || !hasVideo || typeof window === "undefined") return;
    const query = window.matchMedia(PHONE_QUERY);
    const sync = () => setPhone(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [still, hasVideo]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (still || !hasVideo || !el) return;
    const unregister = register({
      el,
      setActive: (next) => setActive((current) => (current === next ? current : next)),
    });
    // Cards move continuously inside the marquee, so poll in addition to
    // scroll/resize events.
    const interval = window.setInterval(schedule, 300);
    return () => {
      unregister();
      window.clearInterval(interval);
    };
  }, [still, hasVideo, project.slug]);

  const image = project.cover ? (
    <img
      src={project.cover}
      alt={project.title}
      loading="lazy"
      decoding="async"
      width={1200}
      height={900}
      className={CLASS}
    />
  ) : (
    <div className="h-full w-full bg-gradient-to-br from-muted via-muted/60 to-accent" />
  );

  if (still || !hasVideo) return image;

  // `phone === null` only before the first client effect runs, at which point
  // nothing is mounted yet anyway.
  const source = phone
    ? project.mobileCoverVideo || project.coverVideo
    : project.coverVideo || project.mobileCoverVideo;

  return (
    <div ref={wrapperRef} className="relative h-full w-full">
      {image}
      {source && (active || painted) && (
        <video
          key="cover"
          src={source}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-label={project.title}
          data-portfolio-cover
          onLoadedData={() => setPainted(true)}
          onCanPlay={() => setPainted(true)}
          onPlaying={() => setPainted(true)}
          className={`absolute inset-0 ${CLASS} transition-opacity duration-300 ${
            painted ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
}
